import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { useToast } from '@/shared/hooks/useToast';
import { boardSchema, type BoardFormValues } from '../schemas';
import { useCreateBoard, useUpdateBoard } from '../hooks/useBoardMutations';
import type { Board } from '../types';

interface BoardFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  board?: Board;
}

export function BoardFormDialog({ open, onOpenChange, projectId, board }: BoardFormDialogProps) {
  const { t } = useTranslation('boards');
  const { toast } = useToast();
  const isEditing = !!board;

  const createBoard = useCreateBoard(projectId);
  const updateBoard = useUpdateBoard(projectId);
  const isPending = createBoard.isPending || updateBoard.isPending;

  const { control, handleSubmit, reset } = useForm<BoardFormValues>({
    resolver: zodResolver(boardSchema),
    defaultValues: { name: '', description: '' },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: board?.name ?? '',
        description: board?.description ?? '',
      });
    }
  }, [open, board, reset]);

  function onSubmit(data: BoardFormValues) {
    if (isEditing && board?.id) {
      updateBoard.mutate(
        { boardId: board.id, data },
        {
          onSuccess: () => {
            toast.success({ title: t('toast.updateSuccess') });
            onOpenChange(false);
          },
        },
      );
    } else {
      createBoard.mutate(data, {
        onSuccess: () => {
          toast.success({ title: t('toast.createSuccess') });
          onOpenChange(false);
        },
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full rounded-none md:max-w-[440px] md:rounded-lg"
        data-testid="boards-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? t('actions.edit') : t('actions.create')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('dialogs.boardFormDescription')}
          </DialogDescription>
        </DialogHeader>

        <form
          id="board-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 py-2"
        >
          <FormFieldInput
            control={control}
            name="name"
            label={t('form.name')}
            floatingLabel
            disabled={isPending}
            data-testid="boards-form-name-input"
          />

          <FormFieldTextarea
            control={control}
            name="description"
            label={t('form.description')}
            floatingLabel
            rows={3}
            disabled={isPending}
            data-testid="boards-form-description-input"
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="board-form"
            disabled={isPending}
            data-testid="boards-form-submit-button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
